import React, { useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { TbUsers, TbTool } from 'react-icons/tb';
import { File } from 'lucide-react';
import { ReceiptText } from 'lucide-react';
import {
  MdRoute,
  MdMonitor,
  MdDirectionsBoat,
  MdSatelliteAlt,
  MdSmartToy,
  MdSwapHoriz,
  MdLocalShipping,
  MdScience,
  MdKeyboardArrowDown,
  MdMarkEmailRead,
  MdCloudQueue,
  MdPublic,
  MdAttachMoney,
  MdTableChart,
  MdViewInAr,
  MdContactMail,
  MdSlideshow,
} from 'react-icons/md';
import { useUser } from './UserContext';
import { hasAnyPermission, hasPermission, Permission } from '../interfaces/Permissions';
import useClickOutside from '../functions/useClickOutside';

type IconType = React.ComponentType<{ className?: string }>;

interface NavItem {
  to: string;
  label: string;
  icon: IconType;
  permissions: Permission[];
}

interface NavGroup {
  label: string;
  icon: IconType;
  items: NavItem[];
}

// Gruparea elementelor din bara de Setări în meniuri dropdown (erau prea multe pe un rând).
// Exportat ca să rămână meniul mobil aliniat cu desktopul.
export const SETTINGS_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Operațiuni',
    icon: MdDirectionsBoat,
    items: [
      { to: '/bookings/ymlRoutes', label: 'Servicii YML', icon: MdRoute, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/yml-p2p', label: 'Rute P2P YML', icon: MdRoute, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/vessel-estimation', label: 'Estimare nave', icon: MdDirectionsBoat, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/import-offer-vessels', label: 'Nave oferte Import', icon: MdDirectionsBoat, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/internal-transport', label: 'Transport intern', icon: MdLocalShipping, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/ports', label: 'Porturi', icon: MdDirectionsBoat, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/port-mirrors', label: 'Porturi clonă', icon: MdSwapHoriz, permissions: ['RATE_PORT_MIRROR_MANAGE'] },
      { to: '/settings/notice', label: 'Avizări exporturi', icon: File, permissions: ['SETTING_VIEW_ALL'] },
    ],
  },
  {
    label: 'Comercial',
    icon: TbUsers,
    items: [
      { to: '/settings/recommendations', label: 'Recomandări', icon: TbUsers, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/container-types', label: 'Tipuri container', icon: MdViewInAr, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/offers', label: 'Oferte', icon: MdLocalShipping, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/client-portal', label: 'Portal client', icon: MdContactMail, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/presentation', label: 'Prezentare', icon: MdSlideshow, permissions: ['PRESENTATION_MANAGE_ALL'] },
      { to: '/settings/invoices', label: 'Facturare', icon: ReceiptText, permissions: ['INVOICE_VIEW_ALL', 'INVOICE_VIEW', 'INVOICE_SETTINGS_MANAGE'] },
      { to: '/settings/dbf', label: 'Vizualizare DBF', icon: MdTableChart, permissions: ['SETTING_VIEW_ALL'] },
    ],
  },
  {
    label: 'Integrări & AI',
    icon: MdSmartToy,
    items: [
      { to: '/settings/datadocked', label: 'DataDocked', icon: MdSatelliteAlt, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/ai-assistant', label: 'Asistent AI', icon: MdSmartToy, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/ai-usage', label: 'Costuri & jurnal AI', icon: MdAttachMoney, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/scrape-do', label: 'Scrape.do', icon: MdCloudQueue, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/geolocation', label: 'Geolocalizare IP', icon: MdPublic, permissions: ['SETTING_VIEW_ALL'] },
    ],
  },
  {
    label: 'Sistem',
    icon: TbTool,
    items: [
      { to: '/settings/tools', label: 'Unelte', icon: TbTool, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/monitoring', label: 'Monitorizare', icon: MdMonitor, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/environment', label: 'Mediu & Automatizări', icon: MdScience, permissions: ['SETTING_VIEW_ALL'] },
      { to: '/settings/email-sync', label: 'Preluare emailuri (IMAP)', icon: MdMarkEmailRead, permissions: ['SETTING_VIEW_ALL'] },
    ],
  },
];

const triggerBase =
  'flex items-center gap-1.5 h-full px-3 rounded-md select-none cursor-pointer transition-colors';
const triggerActive = 'font-bold text-darkprimary dark:text-primary';
const triggerIdle = 'hover:text-darkprimary dark:text-dm-text-secondary dark:hover:text-primary';

const SettingsNavDropdown: React.FC<{ group: NavGroup }> = ({ group }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useClickOutside(ref, () => setOpen(false));

  const GroupIcon = group.icon;
  // Trigger-ul e „activ" dacă suntem pe una din sub-paginile grupului.
  const isGroupActive = group.items.some((item) => location.pathname.startsWith(item.to));

  return (
    <div
      className="relative h-full"
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${triggerBase} ${isGroupActive ? triggerActive : triggerIdle}`}
      >
        <GroupIcon className="h-4 w-4" />
        <span>{group.label}</span>
        <MdKeyboardArrowDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 min-w-[220px] rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-dm-border dark:bg-dm-surface">
          {group.items.map((item) => {
            const ItemIcon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 text-sm ${
                    isActive
                      ? 'bg-softprimary/60 font-semibold text-darkprimary dark:bg-dm-nav-secondary dark:text-primary'
                      : 'text-gray-700 hover:bg-softprimary/40 dark:text-dm-text-secondary dark:hover:bg-dm-nav-secondary dark:hover:text-primary'
                  }`
                }
              >
                <ItemIcon className="h-4 w-4 shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SettingsNavBar: React.FC = () => {
  const { user } = useUser();

  // Bara de Setări e accesibilă DOAR userilor cu permisiune de setări; restul nu o văd deloc.
  if (!hasPermission(user, 'SETTING_VIEW_ALL')) return null;

  // Filtrează elementele după permisiuni și ascunde grupurile rămase goale.
  const visibleGroups = SETTINGS_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => hasAnyPermission(user, item.permissions)),
  })).filter((group) => group.items.length > 0);

  if (visibleGroups.length === 0) return null;

  return (
    <div className="hidden bg-softprimary dark:bg-dm-nav-secondary md:flex items-center px-5 text-black dark:text-dm-text text-[0.95rem] font-medium h-9 ps-[155px]">
      {visibleGroups.map((group) => (
        <SettingsNavDropdown key={group.label} group={group} />
      ))}
    </div>
  );
};

export default SettingsNavBar;
